import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Button, Typography } from "@platform/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { rateQueryKeys } from "../../../utils/query-keys";
import { getPlanHeaderName } from "../../../utils/plan-helpers";
import { isCompanionSchedule } from "../../../utils/schedule-helpers";

import type { QueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type { RateDesignEnum } from "@platform/openapi-types";
import type { Rate, RateSchedule } from "../../../types";
import type { BenefitPlan, BenefitPlanGroup } from "../../../types/models";

export interface RateCopyColumn {
  id: string;
  label: string;
  matches: (rate: Rate) => boolean;
}

// Sentinel column id for a destination with no real EE/SP split (every age
// band holds exactly one rate row). Distinct from any real RateCopyColumn id.
const MERGED_COLUMN_ID = "__merged__";

export const resolveDestinationPlans = (
  plan: BenefitPlan,
  copyDestinationPlans?: BenefitPlan[],
  benefitPlanGroup?: BenefitPlanGroup,
): BenefitPlan[] => {
  const pool = copyDestinationPlans ?? benefitPlanGroup?.benefitPlans ?? [plan];
  const withCurrent = pool.some((p) => p.id === plan.id)
    ? pool
    : [plan, ...pool];
  return withCurrent.filter((p) => p.policyPeriod !== "alternate");
};

interface CopyTargetSelection {
  planId: string;
  columnId: string;
}

interface CrossPlanCopyUpdate {
  scheduleId: string;
  rateId: string;
  benefitPlanId: string;
  value: number | null;
}

type RateMutation = {
  mutateAsync(vars: {
    scheduleId: string;
    rateId: string;
    benefitPlanId: string;
    rate: { rate: number | null };
  }): Promise<unknown>;
};

const ageBandKey = (
  rate: Pick<Rate, "ageBandLower" | "ageBandUpper">,
): string => `${rate.ageBandLower}-${rate.ageBandUpper}`;

// The primary (non-companion) schedule holds the age-banded rates; the
// companion flat schedule is skipped. Mirrors useGetRateScheduleStats so
// sibling plans read from the same shape already in the query cache.
const getPrimarySchedule = (
  schedules: RateSchedule[] | undefined,
): RateSchedule | undefined =>
  schedules?.find((schedule) => !isCompanionSchedule(schedule, schedules));

// Reads a plan's cached primary schedule (populated when that plan's own cell
// renders), avoiding a rules-of-hooks violation from a per-target hook call.
const getCachedPrimarySchedule = (
  queryClient: QueryClient,
  planId: string,
): RateSchedule | undefined =>
  getPrimarySchedule(
    queryClient.getQueryData<RateSchedule[]>(rateQueryKeys.schedules(planId)),
  );

// True when a destination should show a single "Rate" copy target instead of
// separate EE/SP targets. Prefers the persisted flag for EE/SP same-rate
// schedules; falls back to inspecting the saved rate rows for schedules where
// tier tags are missing/unrecognized (so EE/SP matchers would otherwise no-op).
const isMergedDestination = (
  schedule: RateSchedule | undefined,
  eeColumn: RateCopyColumn | undefined,
  spColumn: RateCopyColumn | undefined,
): boolean => {
  if (!schedule) return false;
  if (schedule.empSpouseRatesDiffer) {
    return schedule.empSpouseRatesDiffer === "no";
  }
  const rates = schedule.rates ?? [];
  if (rates.length === 0) return false;
  return !rates.some((r) => eeColumn?.matches(r) || spColumn?.matches(r));
};

// Builds the rate PATCHes for each selected target. `matches` picks the
// destination row(s): the real column matcher, or "the one row" if merged.
const buildCrossPlanCopyUpdates = (
  sourceValuesByBand: Record<string, number | null>,
  targets: {
    planId: string;
    schedule?: RateSchedule;
    matches: (rate: Rate) => boolean;
  }[],
): CrossPlanCopyUpdate[] => {
  const updates: CrossPlanCopyUpdate[] = [];
  targets.forEach(({ planId, schedule, matches }) => {
    if (!schedule?.id) return;
    (schedule.rates ?? []).forEach((rate) => {
      if (!matches(rate) || !rate.id) return;
      const key = ageBandKey(rate);
      if (!(key in sourceValuesByBand)) return;
      const value = sourceValuesByBand[key];
      if (value === (rate.rate ?? null)) return;
      updates.push({
        scheduleId: schedule.id,
        rateId: rate.id,
        benefitPlanId: planId,
        value,
      });
    });
  });
  return updates;
};

interface UseAgeBandedRateCopyParams {
  plan: BenefitPlan;
  rates: Rate[];
  rateSchedule?: RateSchedule;
  /** Rate columns available in the current layout, offered as copy targets. */
  columns: RateCopyColumn[];
  /** Rate design of this renderer, used to skip destinations of other designs. */
  rateDesign: RateDesignEnum;
  destinationPlans: BenefitPlan[];
  updateRateMutation: RateMutation;
  updateProposalRateMutation: RateMutation;
  onRateSaved?: () => void;
}

interface UseAgeBandedRateCopyResult {
  /** Opens the copy dialog with the given column as the source. */
  openCopy: (columnId: string) => void;
  /** A copy icon button for a rate-column header. */
  renderCopyButton: (columnId: string, label: string) => ReactNode;
  /** The copy dialog; render once per layout. */
  copyDialog: ReactNode;
}

/**
 * Shared cross-plan rate-copy behavior for age-banded renderers. Provides a
 * per-column copy icon and a dialog that copies a source column's per-band
 * rates onto any selected column of any destination plan in the coverage.
 */
export const useAgeBandedRateCopy = ({
  plan,
  rates,
  rateSchedule,
  columns,
  rateDesign,
  destinationPlans,
  updateRateMutation,
  updateProposalRateMutation,
  onRateSaved,
}: UseAgeBandedRateCopyParams): UseAgeBandedRateCopyResult => {
  const queryClient = useQueryClient();

  const [copySourceId, setCopySourceId] = useState<string | null>(null);
  const [selectedTargets, setSelectedTargets] = useState<CopyTargetSelection[]>(
    [],
  );
  const [isCopying, setIsCopying] = useState(false);

  const columnsById = useMemo(() => {
    const map: Record<string, RateCopyColumn> = {};
    columns.forEach((column) => {
      map[column.id] = column;
    });
    return map;
  }, [columns]);

  const plansById = useMemo(() => {
    const map: Record<string, BenefitPlan> = {};
    destinationPlans.forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [destinationPlans]);

  const isEeSpColumnPair =
    columns.length === 2 &&
    columns.some((c) => c.id === "ee") &&
    columns.some((c) => c.id === "sp");

  const closeCopyDialog = useCallback((): void => {
    setCopySourceId(null);
    setSelectedTargets([]);
  }, []);

  const isTargetSelected = useCallback(
    (planId: string, columnId: string): boolean =>
      selectedTargets.some(
        (t) => t.planId === planId && t.columnId === columnId,
      ),
    [selectedTargets],
  );

  const toggleTarget = useCallback((planId: string, columnId: string): void => {
    setSelectedTargets((prev) =>
      prev.some((t) => t.planId === planId && t.columnId === columnId)
        ? prev.filter((t) => !(t.planId === planId && t.columnId === columnId))
        : [...prev, { planId, columnId }],
    );
  }, []);

  // Copy the source column's per-band values onto every selected destination
  // target. Reads each destination's cached schedule to resolve rate IDs,
  // fires the differing PATCHes in parallel, and refreshes once when they
  // resolve.
  const handleConfirmCopy = useCallback(async (): Promise<void> => {
    const sourceColumn = copySourceId ? columnsById[copySourceId] : undefined;
    if (!sourceColumn || selectedTargets.length === 0) {
      closeCopyDialog();
      return;
    }

    const sourceValuesByBand: Record<string, number | null> = {};
    rates.forEach((rate) => {
      if (sourceColumn.matches(rate)) {
        sourceValuesByBand[ageBandKey(rate)] = rate.rate ?? null;
      }
    });

    const targets = selectedTargets.map(({ planId, columnId }) => {
      const schedule =
        planId === plan.id
          ? rateSchedule
            ? { ...rateSchedule, rates }
            : undefined
          : getCachedPrimarySchedule(queryClient, planId);

      if (columnId === MERGED_COLUMN_ID) {
        return { planId, schedule, matches: () => true };
      }

      const column = columnsById[columnId];
      return { planId, schedule, matches: column?.matches ?? (() => false) };
    });

    const updates = buildCrossPlanCopyUpdates(sourceValuesByBand, targets);
    if (updates.length === 0) {
      closeCopyDialog();
      return;
    }

    setIsCopying(true);
    try {
      await Promise.all(
        updates.map((update) => {
          const targetPlan = plansById[update.benefitPlanId];
          const mutation =
            targetPlan?.rateScheduleSource === "proposal"
              ? updateProposalRateMutation
              : updateRateMutation;
          return mutation.mutateAsync({
            scheduleId: update.scheduleId,
            rateId: update.rateId,
            benefitPlanId: update.benefitPlanId,
            rate: { rate: update.value },
          });
        }),
      );
      onRateSaved?.();
    } finally {
      setIsCopying(false);
      closeCopyDialog();
    }
  }, [
    copySourceId,
    columnsById,
    selectedTargets,
    rates,
    plan.id,
    rateSchedule,
    queryClient,
    plansById,
    updateProposalRateMutation,
    updateRateMutation,
    onRateSaved,
    closeCopyDialog,
  ]);

  const openCopy = useCallback((columnId: string): void => {
    setCopySourceId(columnId);
  }, []);

  const renderCopyButton = useCallback(
    (columnId: string, label: string): ReactNode => (
      <Tooltip title={`Copy ${label} to other columns`}>
        <IconButton
          size="small"
          aria-label={`Copy ${label} to other columns`}
          onClick={() => openCopy(columnId)}
          sx={{ p: 0.25 }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    ),
    [openCopy],
  );

  const copySourceLabel = copySourceId
    ? (columnsById[copySourceId]?.label ?? "")
    : "";

  const copyDialog = (
    <Dialog
      open={copySourceId !== null}
      onClose={() => (isCopying ? undefined : closeCopyDialog())}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Copy {copySourceLabel} rates</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          Choose where to paste the {copySourceLabel} rates. Only age bands that
          exist in the destination are updated; the rest are skipped.
        </DialogContentText>
        {destinationPlans.map((destPlan) => {
          const primarySchedule =
            destPlan.id === plan.id
              ? rateSchedule
              : getCachedPrimarySchedule(queryClient, destPlan.id);
          const destDesign = primarySchedule?.rateDesign;
          if (!destDesign || destDesign !== rateDesign) {
            return null;
          }
          const planName = getPlanHeaderName(destPlan) || destPlan.identifier;
          const isSource = (columnId: string): boolean =>
            destPlan.id === plan.id && columnId === copySourceId;
          const isMerged =
            isEeSpColumnPair &&
            isMergedDestination(
              primarySchedule,
              columnsById["ee"],
              columnsById["sp"],
            );
          return (
            <Box key={destPlan.id} sx={{ mb: 0.5 }}>
              <Typography variant="body-medium">
                <strong>{planName}</strong>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, pl: 1 }}>
                {isMerged ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={isTargetSelected(
                          destPlan.id,
                          MERGED_COLUMN_ID,
                        )}
                        disabled={isCopying}
                        onChange={() =>
                          toggleTarget(destPlan.id, MERGED_COLUMN_ID)
                        }
                        inputProps={{
                          "aria-label": `Copy to ${planName} Rate`,
                        }}
                      />
                    }
                    label="Rate"
                  />
                ) : (
                  columns.map((column) => (
                    <FormControlLabel
                      key={column.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={isTargetSelected(destPlan.id, column.id)}
                          disabled={isSource(column.id) || isCopying}
                          onChange={() => toggleTarget(destPlan.id, column.id)}
                          inputProps={{
                            "aria-label": `Copy to ${planName} ${column.label}`,
                          }}
                        />
                      }
                      label={column.label}
                    />
                  ))
                )}
              </Box>
            </Box>
          );
        })}
        <DialogContentText sx={{ mt: 1, color: "warning.main" }}>
          This overwrites the selected columns and can&apos;t be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={closeCopyDialog}
          disabled={isCopying}
          variant="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmCopy}
          disabled={isCopying || selectedTargets.length === 0}
          variant="primary"
        >
          {isCopying ? "Copying…" : "Copy"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { openCopy, renderCopyButton, copyDialog };
};
